export interface UserMetadata {
  ipAddress?: string;
  browser: string;
  operatingSystem: string;
  timestamp: Date;
  country?: string;
  city?: string;
  town?: string;
  userAgent: string;
  screenResolution: string;
  timezone: string;
  language: string;
}

export class UserMetadataService {
  // Collect user metadata during signup/login
  static async collectUserMetadata(): Promise<UserMetadata> {
    const metadata: UserMetadata = {
      browser: this.getBrowserInfo(),
      operatingSystem: this.getOperatingSystem(),
      timestamp: new Date(),
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
    };

    // Try to get IP and location data
    try {
      const ipData = await this.getIPAndLocation();
      if (ipData) {
        metadata.ipAddress = ipData.ip;
        metadata.country = ipData.country;
        metadata.city = ipData.city;
        metadata.town = ipData.town;
      }
    } catch (error) {
      console.warn('Failed to get IP and location data:', error);
    }

    return metadata;
  }

  private static getBrowserInfo(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    
    return 'Unknown Browser';
  }

  private static getOperatingSystem(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    
    return 'Unknown OS';
  }

  private static async getIPAndLocation(): Promise<{
    ip: string;
    country: string;
    city: string;
    town: string;
  } | null> {
    try {
      // Using a free IP geolocation service
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) throw new Error('Failed to fetch location data');
      
      const data = await response.json();
      return {
        ip: data.ip,
        country: data.country_name || '',
        city: data.city || '',
        town: data.region || ''
      };
    } catch (error) {
      console.warn('Failed to get IP and location:', error);
      return null;
    }
  }

  // Validate password strength
  static validatePasswordStrength(password: string): {
    isStrong: boolean;
    score: number;
    feedback: string[];
  } {
    const feedback: string[] = [];
    let score = 0;

    // Length check
    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('Password should be at least 8 characters long');
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include at least one uppercase letter');
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include at least one lowercase letter');
    }

    // Number check
    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include at least one number');
    }

    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include at least one special character');
    }

    const isStrong = score >= 4;
    
    if (!isStrong && feedback.length === 0) {
      feedback.push('Password should be stronger');
    }

    return {
      isStrong,
      score,
      feedback
    };
  }
}
